import { Info, PartyPopper, Trash2 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { View, XStack } from "tamagui";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { Block } from "../../ui/Block";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { CenterContent } from "../../ui/CenterContent";
import { Group } from "../../ui/Group";
import { Label } from "../../ui/Label";
import { Link } from "../../ui/Link";
import { PopoverConfirm } from "../../ui/PopoverConfirm";
import { Screen } from "../../ui/Screen";
import { Title } from "../../ui/Title";
import { SignInSignOutBox } from "../SignInSignOutBox";
import { useStores } from "../stores";
import { isRunningInApp } from "../utils";

export const HomeScreen = observer(() => {
  const { t } = useTranslation();

  const { navigate } = usePlanNavigation();
  const { sessionStore } = useStores();

  return (
    <Screen>
      <View>
        {sessionStore.superUser?.data.businessPlans.length > 0 && (
          <Box>
            <Title>Your Events</Title>

            <Group>
              {sessionStore.superUser?.data.businessPlans.map(
                ({
                  businessPlanUid,
                  title,
                }: {
                  businessPlanUid: string;
                  title: string;
                }) => {
                  return (
                    <Block key={businessPlanUid} grouped>
                      <XStack
                        width={"100%"}
                        justifyContent="space-between"
                        alignItems="center"
                        gap="$space.medium"
                      >
                        <XStack
                          flex={1}
                          cursor="pointer"
                          justifyContent="space-between"
                          alignItems="center"
                          gap="$space.small"
                          onPress={() => {
                            navigate("BusinessHome", {
                              businessUid: businessPlanUid,
                            });
                          }}
                        >
                          <View flex={1}>
                            <Label>{title}</Label>
                          </View>
                        </XStack>
                      </XStack>
                    </Block>
                  );
                },
              )}
            </Group>
          </Box>
        )}

        {sessionStore.superUser?.data.plans.length ? (
          <Box>
            <Title>{t("home_your_planiras")}</Title>

            <Group>
              {sessionStore.superUser?.data.plans.map(
                ({ planUid, title }: { planUid: string; title: string }) => {
                  return (
                    <Block key={planUid} grouped>
                      <XStack
                        width={"100%"}
                        justifyContent="space-between"
                        alignItems="center"
                        gap="$space.medium"
                      >
                        <XStack
                          flex={1}
                          cursor="pointer"
                          justifyContent="space-between"
                          alignItems="center"
                          gap="$space.small"
                          onPress={() => {
                            navigate("Plan", { planUid: planUid });
                          }}
                        >
                          <View flex={1}>
                            <Label>{title}</Label>
                          </View>
                        </XStack>

                        <PopoverConfirm
                          onConfirm={() => {
                            sessionStore.superUser?.archivePlanUid(planUid);
                          }}
                        >
                          <Trash2 size={"$size.normal"} color={"$muted"} />
                        </PopoverConfirm>
                      </XStack>
                    </Block>
                  );
                },
              )}
            </Group>
          </Box>
        ) : (
          <Box>
            <CenterContent
              title={t("home_no_planiras")}
              subTitle={`${t("home_no_planiras_message")}${
                sessionStore.isRegistered ? "" : ` ${t("home_sign_in_hint")}`
              }`}
              icon={Info}
            />
          </Box>
        )}
        <Button
          position={"sticky" as "relative"}
          bottom={0}
          onPress={() => navigate("Create", { step: "user" })}
          icon={PartyPopper}
        >
          {t("home_create_new")}
        </Button>
      </View>

      {!isRunningInApp() && <SignInSignOutBox />}

      {__DEV__ ? (
        <Box>
          <Link to={{ screen: "Dev" }}>{"#DevGuide"}</Link>
          <Link to={{ screen: "DevPlans" }}>{"#DevPlansViewer"}</Link>
          <Body>suid: {sessionStore.uid}</Body>
        </Box>
      ) : undefined}
    </Screen>
  );
});
